import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { notification, Modal } from 'antd'
import { useSelector } from 'react-redux'

import {
  BudgetChartTicket,
  FullRequestAir,
  PolicyAction,
  ResumeAirRequestQuoteAirs
} from 'src/types'

import { ApplicationState } from 'src/store'
import { Text, Input } from 'src/components/Ant'
import { useStepsContext } from 'src/views/Requests/views/Form'
import { useT } from 'src/core/hooks/use-translation'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'

import { WrapInputJustification } from '../SearchResultItem/styles'
import OneWay from './one-way'
import MultiStretch from './multi-stretch'
import RoundTrip from './round-trip'

import { budgetTicketAirReduce } from '../../../../../Assignment/components/Budget/budget-summary'

export type PickFareProps = {
  selectedFares: FullRequestAir[]
  onPickFare(fare: FullRequestAir, searchKey: string): void
}

type Props = {
  onPick?(selectedFares: FullRequestAir[]): void
}

function List({ onPick }: Props) {
  const { air } = useSelector((state: ApplicationState) => ({
    air: state.air
  }))

  const [selectedFares, setSelectedFares] = useState<FullRequestAir[]>([])
  const [policyListCheck, setPolicyListCheck] = useState<
    ResumeAirRequestQuoteAirs[]
  >([])

  const { t } = useT()
  const stepsContext = useStepsContext()

  const hasSomeLoading = useMemo<boolean>(() => {
    return (
      Object.keys(air.searches).length &&
      Object.values(air.searches).some(({ loading }) => !loading)
    )
  }, [air.searches])

  const policyRule = useReasonPolicyRule(
    stepsContext?.request?.request_user_data[0]?.reason_id
  )

  const GetBudgetTicketAir = useCallback(
    (fareKeys: string[]) =>
      Object.values(air.searches).reduce(budgetTicketAirReduce(fareKeys), []),
    [air.searches]
  )

  useEffect(() => {
    setSelectedFares([])
  }, [air.searchType])

  useEffect(() => {
    if (selectedFares.length) {
      if (onPick) {
        onPick(selectedFares)
      } else {
        stepsContext.setRequest({
          ...stepsContext.request,
          full_request_air: [...selectedFares]
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFares])

  function testAirpolicy(fare: FullRequestAir, searchKey: string) {
    let policyAction: PolicyAction

    if (
      policyRule &&
      policyRule.policy_request_air_rule &&
      policyRule.policy_request_air_rule?.lowest_fare
    ) {
      let lowerValue = policyListCheck[0].request_quote_air_fares[0].total_value
      let selectedValue = 0

      policyListCheck.forEach(quoteAirItem => {
        quoteAirItem.request_quote_air_fares.forEach(fareItem => {
          if (fareItem.total_value < lowerValue) {
            lowerValue = fareItem.total_value
          }

          if (
            fare.request_quote_air_fare_hash[0] ===
            fareItem.request_quote_fare_hash
          ) {
            selectedValue = fareItem.total_value
          }
        })
      })

      if (selectedValue > lowerValue) {
        policyAction = policyRule.policy_request_air_rule.action
      }
    }

    switch (policyAction) {
      case 'ALERT':
        Modal.confirm({
          cancelText: <Text onlyText title="common.actions.cancel" />,
          content: (
            <WrapInputJustification>
              <Text title="web.services.airs.validation.policy.min-fare.alert.content" />
              <Input
                allowClear
                name="minFareAirJustification"
                placeholder="web.services.hotels.validation.policy.min-fare.alert.justification.placeholder"
                size="large"
              />
            </WrapInputJustification>
          ),
          okText: <Text onlyText title="common.actions.yes" />,
          title: (
            <Text
              onlyText
              title="web.services.airs.validation.policy.min-fare.alert.title"
            />
          ),
          onCancel() {},
          onOk() {
            const justification: any = document.querySelector(
              "input[name='minFareAirJustification']"
            )

            if (justification?.value.trim().length) {
              pickFare(
                { ...fare, justification: [justification.value] },
                searchKey
              )
            }
          }
        })
        break
      case 'BLOCK':
        Modal.error({
          content: (
            <Text
              onlyText
              title="web.services.airs.validation.policy.min-fare.block.content"
            />
          ),
          onOk() {},
          okText: <Text onlyText title="common.actions.close" />,
          title: (
            <Text
              onlyText
              title="web.services.airs.validation.policy.min-fare.block.title"
            />
          )
        })
        break
      default:
        pickFare(fare, searchKey)
        break
    }
  }

  function pickFare(fare: FullRequestAir, searchKey: string) {
    let tickets: BudgetChartTicket[] = []

    if (!selectedFares.length || air.searchType === 'ONE_WAY') {
      setSelectedFares([fare])
      if (stepsContext.budgetChartAllocations.active) {
        tickets = GetBudgetTicketAir(fare.request_quote_air_fare_hash)
      }
    } else if (air.searchType === 'MULTI-STRETCH') {
      const indexSearch = Object.keys(air.searches).findIndex(
        key => key === searchKey
      )

      const copySelectedFares = [...selectedFares]
      copySelectedFares[indexSearch] = fare

      if (stepsContext.budgetChartAllocations.active) {
        tickets = GetBudgetTicketAir(
          copySelectedFares.map(
            ({ request_quote_air_fare_hash }) => request_quote_air_fare_hash[0]
          )
        )
      }
      setSelectedFares(copySelectedFares)
    } else {
      const request_quote_air_fare_hash = [
        selectedFares[0].request_quote_air_fare_hash[0],
        fare.request_quote_air_fare_hash[0]
      ]
      if (stepsContext.budgetChartAllocations.active) {
        tickets = GetBudgetTicketAir(request_quote_air_fare_hash)
      }
      setSelectedFares([
        {
          ...selectedFares[0],
          justification: [
            ...selectedFares[0].justification,
            ...fare.justification
          ],
          request_quote_air_fare_hash,
          request_quote_air_hash: [
            selectedFares[0].request_quote_air_hash[0],
            fare.request_quote_air_hash[0]
          ]
        }
      ])
    }

    const travelers = stepsContext.request?.request_user_data.length || 0

    if (tickets.length && travelers > 1) {
      const getOtherTicketsByTravelers = []
      for (let i = 1; i < travelers; i += 1) {
        getOtherTicketsByTravelers.push(...tickets)
      }
      tickets.push(...getOtherTicketsByTravelers)
    }

    stepsContext.setBudgetAllocations({ standard_type: 'AIR', tickets })
    notification.info({
      message: `${t('web.services.airs.title')} ${t(
        'common.actions.selected'
      )}`,
      placement: 'bottomRight'
    })
  }

  if (hasSomeLoading) {
    return (
      <>
        {air.searchType === 'MULTI-STRETCH' && (
          <MultiStretch
            policyRule={policyRule}
            selectedFares={selectedFares}
            onPickFare={testAirpolicy}
            setPolicyListCheck={setPolicyListCheck}
            // setSearchKey={key => setSearchKey(key)}
          />
        )}
        {air.searchType === 'ONE_WAY' && (
          <OneWay
            selectedFares={selectedFares}
            onPickFare={testAirpolicy}
            setPolicyListCheck={setPolicyListCheck}
            // setSearchKey={key => setSearchKey(key)}
          />
        )}
        {air.searchType === 'ROUND_TRIP' && (
          <RoundTrip
            selectedFares={selectedFares}
            onPickFare={testAirpolicy}
            setPolicyListCheck={setPolicyListCheck}
            // setSearchKey={key => setSearchKey(key)}
          />
        )}
      </>
    )
  }
  return <div />
}

export default List
